import { ProgressSpinner } from "primereact/progressspinner";
import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Select from "react-select";
import Http from "../../../Common/Http";

// Quill editor configuration
const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ["bold", "italic", "underline"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const quillFormats = ["header", "bold", "italic", "underline", "color", "background", "list", "bullet", "link"];

const Create = ({ onSuccess, initialStoryId }) => {
  const [inputs, setInputs] = useState({
    title: "",
    description: "",
    story_id: initialStoryId || null,
    project_id: null,
    task_type_id: null,
    points: 0,
    label_id: null,
    status: "To Do",
    developer_ids: [],
    // Planned dates are optional — leave empty to create an unscheduled task.
    start_date: "",
    end_date: "",
    estimated_time: "",
  });

  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(true);
  const [error, setError] = useState("");

  // Options for select dropdowns
  const [projects, setProjects] = useState([]);
  const [stories, setStories] = useState([]);
  const [developers, setDevelopers] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [labels, setLabels] = useState([]);

  // Status options for dropdown
  const statusOptions = [
    { label: "To Do", value: "To Do" },
    { label: "In Progress", value: "In Progress" },
    { label: "Review", value: "Review" },
    { label: "Done", value: "Done" },
  ];

  // Fetch initial data for dropdowns
  useEffect(() => {
    const fetchFormData = async () => {
      setFormLoading(true);
      setError("");
      let hasError = false;

      try {
        // Fetch projects
        const projectsRes = await Http.get("/project");
        if (projectsRes.data && projectsRes.data.data) {
          setProjects(
            projectsRes.data.data.map((project) => ({
              label: project.title,
              value: project.id,
            }))
          );
        } else {
          console.warn("No projects data found");
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
        hasError = true;
      }

      try {
        // Fetch stories
        const storiesRes = await Http.get("/story");
        if (storiesRes.data && storiesRes.data.data) {
          const formattedStories = storiesRes.data.data.map((story) => ({
            label: story.title,
            value: story.id,
            project_id: story.project_id,
          }));

          setStories(formattedStories);

          // If initialStoryId is provided, set the project_id based on the story
          if (initialStoryId) {
            const selectedStory = formattedStories.find((s) => s.value === initialStoryId);
            if (selectedStory && selectedStory.project_id) {
              setInputs((prev) => ({
                ...prev,
                project_id: selectedStory.project_id,
              }));
            }
          }
        } else {
          console.warn("No stories data found");
        }
      } catch (error) {
        console.error("Error fetching stories:", error);
        // Continue even if stories can't be fetched
      }

      try {
        // Fetch developers
        const developersRes = await Http.get("/staff-department-wise/30");
        if (Array.isArray(developersRes.data)) {
          setDevelopers(
            developersRes.data.map((dev) => ({
              label: dev.name || `${dev.first_name || ""} ${dev.last_name || ""}`.trim() || `ID: ${dev.id}`,
              value: dev.id,
            }))
          );
        } else {
          console.warn("No developers data found");
        }
      } catch (error) {
        console.error("Error fetching developers:", error);
        // Continue even if developers can't be fetched
      }

      try {
        // Fetch task types
        const taskTypesRes = await Http.get("/task-type");
        if (taskTypesRes.data && taskTypesRes.data.data) {
          setTaskTypes(
            taskTypesRes.data.data.map((type) => ({
              label: type.task_type,
              value: type.id,
              icon: type.icon,
              color: type.color,
            }))
          );
        } else {
          console.warn("No task types data found");
        }
      } catch (error) {
        console.error("Error fetching task types:", error);
        hasError = true;
      }

      try {
        // Fetch labels
        const labelsRes = await Http.get("/label");
        if (labelsRes.data && labelsRes.data.data) {
          setLabels(
            labelsRes.data.data.map((label) => ({
              label: label.name,
              value: label.id,
              color: label.color,
            }))
          );
        } else {
          console.warn("No labels data found");
        }
      } catch (error) {
        console.error("Error fetching labels:", error);
        hasError = true;
      }

      if (hasError) {
        setError("Some form data could not be loaded. You may still create a task but some options might be missing.");
      }

      setFormLoading(false);
    };

    fetchFormData();
  }, [initialStoryId]);

  // Handle basic input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  // Handle rich text editor changes
  const handleQuillChange = (content) => {
    setInputs((prev) => ({ ...prev, description: content }));
  };

  // Handle Select dropdowns
  const handleSelectChange = (selectedOption, { name }) => {
    setInputs((prev) => ({ ...prev, [name]: selectedOption ? selectedOption.value : null }));

    // If story is selected, also set the project_id from the story
    if (name === "story_id" && selectedOption) {
      const story = stories.find((s) => s.value === selectedOption.value);
      if (story && story.project_id) {
        setInputs((prev) => ({ ...prev, project_id: story.project_id }));
      }
    }
  };

  // Handle multi-select for developers
  const handleDevelopersChange = (selectedOptions) => {
    setInputs((prev) => ({
      ...prev,
      developer_ids: selectedOptions ? selectedOptions.map((option) => option.value) : [],
    }));
  };

  // Custom template for colored items (task types and labels)
  const coloredItemTemplate = (option) => {
    if (!option) return null;

    return (
      <div className="d-flex align-items-center">
        <div
          style={{
            width: "15px",
            height: "15px",
            backgroundColor: option.color || "#ccc",
            borderRadius: "3px",
            marginRight: "8px",
          }}
        ></div>
        {option.icon && <i className={option.icon} style={{ marginRight: "8px" }}></i>}
        <span>{option.label}</span>
      </div>
    );
  };

  // Form validation and submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Form validation
    if (!inputs.title.trim()) {
      setError("Task title is required");
      setLoading(false);
      return;
    }

    if (!inputs.project_id && !inputs.story_id) {
      setError("Either a project or a story must be selected");
      setLoading(false);
      return;
    }

    // Planned dates are optional (unscheduled tasks). Only enforce that an end
    // date has a start date, and that the range is ordered when both are set.
    if (inputs.end_date && !inputs.start_date) {
      setError("Add a start date when setting an end date");
      setLoading(false);
      return;
    }

    if (
      inputs.start_date &&
      inputs.end_date &&
      new Date(inputs.end_date) < new Date(inputs.start_date)
    ) {
      setError("End date cannot be earlier than start date");
      setLoading(false);
      return;
    }

    try {
      const response = await Http.post("/task", inputs);
      console.log("Task created successfully:", response.data);

      if (onSuccess) {
        // Pass the newly created task data back to the parent component
        onSuccess(response.data.data || response.data);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error creating task:", error);
      setError(error.response?.data?.message || "Failed to create task. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div>
      {loading || formLoading ? (
        <div className="text-center p-5">
          <ProgressSpinner />
          <p className="mt-2">{formLoading ? "Loading form data..." : "Creating task..."}</p>
        </div>
      ) : (
        <div className="container-fluid">
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Basic Fields */}
            <div className="row mb-3">
              <div className="col-lg-12">
                <label htmlFor="title" className="form-label">
                  Task Title <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="title"
                  id="title"
                  value={inputs.title}
                  onChange={handleInputChange}
                  placeholder="Enter task title"
                  required
                />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <label htmlFor="story_id" className="form-label">
                  Story (Optional)
                </label>
                <Select
                  id="story_id"
                  name="story_id"
                  value={stories.find((option) => option.value === inputs.story_id) || null}
                  options={stories}
                  onChange={(option, action) => handleSelectChange(option, action)}
                  placeholder="Select a story"
                  isClearable
                  className="basic-select"
                  classNamePrefix="select"
                />
                <small className="text-muted">If selected, this task will be part of the story</small>
              </div>
              <div className="col-md-6">
                <label htmlFor="project_id" className="form-label">
                  Project {!inputs.story_id && <span className="text-danger">*</span>}
                </label>
                <Select
                  id="project_id"
                  name="project_id"
                  value={projects.find((option) => option.value === inputs.project_id) || null}
                  options={projects}
                  onChange={(option, action) => handleSelectChange(option, action)}
                  placeholder="Select a project"
                  isDisabled={!!inputs.story_id}
                  className="basic-select"
                  classNamePrefix="select"
                />
                <small className="text-muted">
                  {inputs.story_id ? "Project is determined by the selected story" : "Required if no story is selected"}
                </small>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-lg-12">
                <label htmlFor="description" className="form-label">
                  Description
                </label>
                <ReactQuill
                  theme="snow"
                  value={inputs.description}
                  onChange={handleQuillChange}
                  placeholder="Enter task description"
                  modules={quillModules}
                  formats={quillFormats}
                  style={{ height: "150px", marginBottom: "40px" }}
                />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <label htmlFor="task_type_id" className="form-label">
                  Task Type
                </label>
                <Select
                  id="task_type_id"
                  name="task_type_id"
                  value={taskTypes.find((option) => option.value === inputs.task_type_id) || null}
                  options={taskTypes}
                  onChange={(option, action) => handleSelectChange(option, action)}
                  placeholder="Select task type"
                  isClearable
                  className="basic-select"
                  classNamePrefix="select"
                  formatOptionLabel={coloredItemTemplate}
                />
              </div>
              <div className="col-md-6">
                <label htmlFor="label_id" className="form-label">
                  Label
                </label>
                <Select
                  id="label_id"
                  name="label_id"
                  value={labels.find((option) => option.value === inputs.label_id) || null}
                  options={labels}
                  onChange={(option, action) => handleSelectChange(option, action)}
                  placeholder="Select a label"
                  isClearable
                  className="basic-select"
                  classNamePrefix="select"
                  formatOptionLabel={coloredItemTemplate}
                />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <label htmlFor="status" className="form-label">
                  Status
                </label>
                <Select
                  id="status"
                  name="status"
                  value={statusOptions.find((option) => option.value === inputs.status) || statusOptions[0]}
                  options={statusOptions}
                  onChange={(option, action) => handleSelectChange(option, action)}
                  placeholder="Select status"
                  className="basic-select"
                  classNamePrefix="select"
                />
              </div>
              <div className="col-md-6">
                <label htmlFor="points" className="form-label">
                  Points
                </label>
                <input type="number" className="form-control" name="points" id="points" value={inputs.points} onChange={handleInputChange} min="0" />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <label htmlFor="start_date" className="form-label">
                  Start Date
                </label>
                <input
                  type="date"
                  className="form-control"
                  name="start_date"
                  id="start_date"
                  value={inputs.start_date}
                  onChange={handleInputChange}
                />
              </div>
              <div className="col-md-6">
                <label htmlFor="end_date" className="form-label">
                  End Date
                </label>
                <input
                  type="date"
                  className="form-control"
                  name="end_date"
                  id="end_date"
                  value={inputs.end_date}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-12">
                <small className="text-muted">
                  Leave dates empty to keep this task unscheduled — you can plan it later.
                </small>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <label htmlFor="estimated_time" className="form-label">
                  Estimated Time
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="estimated_time"
                  id="estimated_time"
                  value={inputs.estimated_time}
                  onChange={handleInputChange}
                  placeholder="e.g., 2 hours, 3 days"
                />
              </div>
              <div className="col-md-6">
                <label htmlFor="developer_ids" className="form-label">
                  Assign Developers
                </label>
                <Select
                  id="developer_ids"
                  isMulti
                  name="developer_ids"
                  options={developers}
                  value={developers.filter((dev) => inputs.developer_ids.includes(dev.value))}
                  onChange={handleDevelopersChange}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  placeholder="Select developers"
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-12 text-end mt-3">
                <button type="submit" className="btn btn-primary float-end" disabled={loading} onClick={handleSubmit}>
                  {loading ? (
                    <>
                      <ProgressSpinner
                        style={{ width: "20px", height: "20px" }}
                        strokeWidth="8"
                        fill="var(--surface-ground)"
                        animationDuration=".5s"
                      />
                      &nbsp;Creating...
                    </>
                  ) : (
                    "Create Task"
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary float-end me-2"
                  onClick={() => {
                    if (onSuccess) {
                      onSuccess();
                    }
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Create;
